import store from '../../../store/store';
import { BaseComponent } from '../../base-component/base-component';
import { Catalog } from '../../../common/constants/catalog';
import { OrderType } from '../../../common/constants/order-types';
import { ProductCardData } from '../../product-card/product-card-display';
import { StoreState } from '../../../store/store.interface';
import { customElement, property } from 'lit/decorators.js';
import { formattedJoin, isTextContainsHTMLTags } from '../../../common/utils/util';
import { getImageSrcUrl } from '../../../common/utils/product-util';
import { getSanitizedProductTitle, populatePdpLocalStorageObject } from '../../product-card/utils/product-card.util';
import { html, nothing, TemplateResult } from 'lit';
import { ifDefined } from 'lit-html/directives/if-defined.js';
import { CREATE_CUSTOM_CONTRIBUTOR_LABEL } from '../utils/create-custom-product.util';
import { selectCreateRequest } from './store/create-search.selector';

@customElement('mhecomm-create-search-card-view')
export class CreateSearchCardView extends BaseComponent {
  @property({ type: Object })
  product?: ProductCardData;

  @property({ type: Number })
  productIndex?: number;

  @property()
  store?: string;

  renderComp(): TemplateResult | typeof nothing {
    return this.product
      ? html`<div class="card shadow rounded border-0 mb-4">
          <div class="row card-body">
            <div class="col-4 col-sm-3 col-md-2 col-lg-2 d-flex align-items-start justify-content-center">
              <a
                tabindex="-1"
                href="${this.product.url}"
                @click=${(e: Event) => this.dataLayerProductDetail(e)}>
                <img
                  src="${this.product.image}"
                  onerror="this.onerror=null; this.src='${getImageSrcUrl(this.product.isbn13!)}';"
                  class="img-fluid"
                  alt=${this.product.title} />
              </a>
            </div>
            <div class="col-8 col-sm-9 col-lg-10">
              <h3 class="h5">
                <a
                  id="${getSanitizedProductTitle(this.product.isbn13)}"
                  href="${this.product.url}"
                  @click=${(e: Event) => this.dataLayerProductDetail(e)}
                  class="fs-large fw-semibold">
                  ${this.getHighlightedSearchValue('heading-product-title', this.product?.title)}
                </a>
              </h3>
              ${this.getMetadata(this.product)}
            </div>
            <!-- Hiding the add-to-cart component for the bookstore landing page -->
            <!-- <div class="col-12 col-lg-3">
              <mhecomm-add-to-cart
                netPrice=${this.product.netPrice}
                ?showQuantity=${true}
                ?showQuantityInputAsDisabled=${true}
                ?isVirtual=${true}
                businessUnit=${this.product.businessUnit}
                ?showRestrictions=${false}
                ?showComingSoon=${this.product.isPublished}
                isbn=${this.product.isbn}
                isbn13=${this.product.isbn13!}
                store=${ifDefined(this.store)}
                .productTitle=${this.product.title}
                .businessUnit=${this.product.businessUnit}
                .copyRightYear=${this.product.copyRightYear}
                .description=${this.product.description}
                .edition=${this.product.edition}
                .gradeLevel=${this.product.gradeLevels}
                .contributors=${formattedJoin(this.product.contributors!)}
                .productIndex=${this.productIndex! + 1 || 1}
                ?showSpinner=${true}>
              </mhecomm-add-to-cart>
            </div> -->
          </div>
        </div>`
      : nothing;
  }

  /**
   * Gets the metadata specific to Create products.
   *
   * @param product
   */
  private getMetadata(product: ProductCardData): TemplateResult {
    return html`
      ${product.isbn13 ? html`<div><strong>ISBN: </strong>${product.isbn13}</div>` : nothing}
      ${product.contributors
        ? html`<div><strong>${CREATE_CUSTOM_CONTRIBUTOR_LABEL}:</strong> ${formattedJoin(product.contributors!)}</div>`
        : nothing}
      ${product.formatTypeDesc ? html`<div><strong>Format: </strong>${product.formatTypeDesc}</div>` : nothing}
      ${product.copyRightYear ? html`<time><span>© ${product.copyRightYear}</span></time>` : nothing}
    `;
  }

  /**
   * Updates the local storage and emits the event when clicking on the product link or image.
   *
   * @param event
   */
  private dataLayerProductDetail(event: Event): void {
    populatePdpLocalStorageObject(
      event as MouseEvent,
      this,
      this.productIndex!,
      this.product as ProductCardData,
      this.store,
      Catalog.he,
      OrderType.HE_CREATE
    );
  }

  /**
   * Highlights the search value in the product title and also ensures that any HTML tags in the title are rendered correctly.
   *
   * @param id
   * @param text
   */
  private getHighlightedSearchValue(id: string, text: string): TemplateResult {
    let htmlString: string;
    const escapeRegExp = (str: string) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const newElement: HTMLElement = document?.createElement('span');
    const searchValue = selectCreateRequest(store.getState() as StoreState)?.searchValue?.trim();

    if (searchValue && text) {
      const searchTermsGroups = searchValue
        .split(' ')
        .map((v) => `(${escapeRegExp(v)})`)
        .join('|');

      const regex = new RegExp(searchTermsGroups, 'gi');

      htmlString =
        '<span>' + text.replace(regex, (searchValue) => `<span class='fw-bold'>${searchValue}</span>`) + '<span>';
      newElement.innerHTML = htmlString;

      return html`${newElement}`;
    }

    if (text && isTextContainsHTMLTags(text)) {
      htmlString = '<span>' + text + '<span>';
      newElement.setAttribute('id', id);
      newElement.innerHTML = htmlString;
      return html`${newElement}`;
    }

    return html`<span id="${id}">${text}</span>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-create-search-card-view': CreateSearchCardView;
  }
}
